import { api } from './client';

export type AutopilotChannel = 'tt' | 'fb' | 'gg' | 'auto';
export type AutopilotDecision = 'pending' | 'approved' | 'rejected';
export type AutopilotActionType = 'circuit' | 'scale' | 'bid' | 'notice';

export interface ImpactItem {
  label: string;
  value: string;
  color?: string;
}

export interface ActionPayload {
  pause?: boolean;
  budget_pct?: number;
  bid_pct?: number;
  new_budget?: number;
  new_bid?: number;
}

export interface SuggestionDetail {
  rule_name?: string;
  knowledge_description?: string;
  target_name?: string;
  platform_account_name?: string;
  account_owner_name?: string;
  channel: AutopilotChannel;
  title: string;
  reason: string;
  urgency: 'high' | 'medium';
  impact: ImpactItem[];
  refs?: string[];
  platform_account_id: string;
  platform_target_id: string;
  execution_target_type?: string;
  execution_platform_target_id?: string;
  action: ActionPayload;
  rule_snapshot?: Record<string, unknown> | null;
  trigger_snapshot?: Array<Record<string, unknown>> | null;
}

export interface ApprovalRecord {
  id: string;
  brand_id: string;
  target_type: 'campaign' | 'ad_group' | 'ad' | 'account';
  target_id: string;
  action_type: AutopilotActionType;
  suggestion: SuggestionDetail;
  decision: AutopilotDecision;
  modified_value?: SuggestionDetail | null;
  approval_note?: string | null;
  created_at: string;
  decided_at?: string | null;
}

export interface OperationLog {
  id: string;
  brand_id: string;
  approval_id?: string | null;
  target_type: string;
  target_id: string;
  action_type: AutopilotActionType;
  original_value?: SuggestionDetail | null;
  modified_value?: SuggestionDetail | null;
  approval_note?: string | null;
  execution_result: 'success' | 'failed' | 'partial' | 'rejected';
  error_message?: string | null;
  executed_by: 'manual' | 'autopilot';
  operator_name?: string;
  duration_ms: number;
  created_at: string;
}

export interface AutopilotRule {
  id?: string;
  brand_id?: string;
  site_id?: string;
  channel: AutopilotChannel;
  enabled: boolean;
  circuit_enabled: boolean;
  circuit_roas_threshold?: number | null;
  circuit_window_hours: number;
  scale_enabled: boolean;
  scale_roas_target?: number | null;
  scale_window_hours: number;
  scale_budget_pct: number;
  scale_max_daily_budget?: number | null;
  bid_enabled: boolean;
  bid_adjust_pct: number;
  bid_check_hours: number;
}

export interface AutoRuleRun {
  id: string;
  brand_id: string;
  site_id: string;
  knowledge_id: string;
  rule_name: string;
  knowledge_description?: string | null;
  target_name?: string | null;
  platform_account_id?: string | null;
  platform_account_name?: string | null;
  account_owner_name?: string | null;
  target_type: string;
  target_id: string;
  campaign_id?: string | null;
  ad_set_id?: string | null;
  ad_id?: string | null;
  platform: string;
  object_level: string;
  evaluation_window?: Record<string, unknown> | null;
  trigger_snapshot?: Array<Record<string, unknown>> | null;
  action_snapshot?: Record<string, unknown> | null;
  decision: string;
  approval_record_id?: string | null;
  executed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutoRuleExecutionSummary {
  processed_rules: number;
  evaluated: number;
  matched: number;
  created: number;
  dedup_skipped: number;
}

export type AutoRuleExecutionJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface AutoRuleExecutionJob {
  id: string;
  brand_id: string;
  site_id: string;
  source: 'manual' | 'scheduled';
  status: AutoRuleExecutionJobStatus;
  summary?: AutoRuleExecutionSummary | null;
  error_message?: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at: string;
}

export interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
}

export interface PagedList<T> {
  list: T[];
  total: number;
  page: number;
  size: number;
}

export interface QueueList extends PagedList<ApprovalRecord> {
  channel_counts?: Partial<Record<AutopilotChannel, number>>;
}

export const autopilotApi = {
  getQueue: (params?: { brand_id?: string; decision?: string; site_id?: string; page?: number; size?: number }) =>
    api.get<ApiEnvelope<QueueList>>('/autopilot/queue', { params }),

  execute: (brandID?: string, siteID?: string) =>
    api.post<ApiEnvelope<AutoRuleExecutionJob>>('/autopilot/execute', undefined, {
      params: brandID ? { brand_id: brandID, site_id: siteID } : undefined,
    }),

  getExecution: (id: string, brandID?: string, siteID?: string) =>
    api.get<ApiEnvelope<AutoRuleExecutionJob>>(`/autopilot/executions/${id}`, {
      params: brandID ? { brand_id: brandID, site_id: siteID } : undefined,
    }),

  getLatestExecution: (brandID?: string, siteID?: string) =>
    api.get<ApiEnvelope<AutoRuleExecutionJob | null>>('/autopilot/executions/latest', {
      params: brandID ? { brand_id: brandID, site_id: siteID } : undefined,
    }),

  approve: (id: string, modified_value?: SuggestionDetail, note?: string) =>
    api.post<ApiEnvelope<{ message: string }>>(
      `/autopilot/${id}/approve`,
      modified_value || note ? { modified_value, note } : {}
    ),

  reject: (id: string, reason?: string, note?: string) =>
    api.post<ApiEnvelope<{ message: string }>>(`/autopilot/${id}/reject`, reason || note ? { reason, note } : {}),

  getLogs: (params?: { brand_id?: string; target_type?: string; page?: number; size?: number }) =>
    api.get<ApiEnvelope<PagedList<OperationLog>>>('/autopilot/logs', { params }),

  getRuleRuns: (params?: {
    brand_id?: string;
    site_id?: string;
    channel?: AutopilotChannel;
    action_type?: AutopilotActionType;
    page?: number;
    size?: number;
  }) =>
    api.get<ApiEnvelope<PagedList<AutoRuleRun>>>('/autopilot/rule-runs', { params }),

  getRules: (brandID?: string, siteID?: string) =>
    api.get<ApiEnvelope<{ list: AutopilotRule[] }>>('/autopilot/rules', { params: brandID ? { brand_id: brandID, site_id: siteID } : undefined }),

  upsertRule: (channel: AutopilotChannel, rule: Partial<AutopilotRule>) =>
    api.put<ApiEnvelope<AutopilotRule>>(`/autopilot/rules/${channel}`, rule),

};
